import React from 'react';
import { Disc3, Radio, Heart, Sparkles } from 'lucide-react';
import { ABOUT } from '@/constants/testIds';

const GENRES = [
  '40s Hits', '50s Classics', '60s Soul', '70s Grooves', '80s Pop',
  'Oldies', 'Jazz', 'Swing', 'Rock and Roll', 'Blues',
  'Country', 'Doo Wop', 'Big Band', 'Easy Listening',
];

const Marquee = () => (
  <div data-testid={ABOUT.genreMarquee} className="relative py-10 border-y border-amber/10 overflow-hidden bg-espresso/40">
    <div className="flex animate-marquee whitespace-nowrap">
      {[...GENRES, ...GENRES].map((g, i) => (
        <div key={i} className="flex items-center gap-8 px-8">
          <span className="font-display text-4xl md:text-5xl text-cream/90 hover:text-amber transition-colors">
            {g}
          </span>
          <Disc3 className="w-6 h-6 text-amber/60" />
        </div>
      ))}
    </div>
  </div>
);

const Stat = ({ value, label }) => (
  <div className="text-center">
    <div className="font-display text-4xl md:text-5xl text-amber">{value}</div>
    <div className="mt-2 text-[10px] tracking-[0.25em] uppercase text-cream/60 font-bold">{label}</div>
  </div>
);

const About = () => {
  return (
    <div data-testid={ABOUT.page} className="pt-28">
      {/* Hero */}
      <section className="relative py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <div className="text-[10px] tracking-[0.25em] uppercase text-amber font-bold mb-4">
              Our Story
            </div>
            <h1 className="font-display text-5xl md:text-7xl text-cream leading-[0.95]">
              A love letter
              <br />
              <span className="font-serif-italic italic text-amber">to the records that raised us.</span>
            </h1>
            <p className="mt-7 text-cream/70 font-body text-lg leading-relaxed max-w-2xl">
              Majestic Jukebox Radio was born in December 2017 from a simple obsession:
              hearing those unforgettable songs the way they were meant to be heard.
              No compression. No filler. No commercials. Just the very best versions, polished
              and put on rotation around the clock.
            </p>
            <p className="mt-5 text-cream/70 font-body text-lg leading-relaxed max-w-2xl">
              We are independent and listener-supported. The kindness of a handful of regulars
              keeps the music on the air, day after day. We&apos;re honoured to have you here.
            </p>
          </div>
          <div className="lg:col-span-5">
            <div className="relative aspect-[4/5] rounded-3xl overflow-hidden border border-amber/20 bg-espresso">
              <img
                src="https://customer-assets.emergentagent.com/job_radio-jukebox-2/artifacts/hp0vmhyh_jukebox-animated-gif.gif"
                alt="A young woman beside a vintage AMI jukebox"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-vinyl/80 via-transparent to-transparent" />
              <div className="absolute bottom-6 left-6 right-6">
                <div className="text-[10px] tracking-[0.25em] uppercase text-amber font-bold mb-2">On air since December 2017</div>
                <div className="font-display text-2xl text-cream">Where every record finds its listener.</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-12 border-y border-amber/10 bg-espresso/30">
        <div className="max-w-5xl mx-auto px-6 md:px-10 grid grid-cols-2 md:grid-cols-4 gap-8">
          <Stat value="24/7" label="On Air" />
          <Stat value="320" label="kbps Quality" />
          <Stat value="14+" label="Genres" />
          <Stat value="0" label="Commercials" />
        </div>
      </section>

      <Marquee />

      {/* Values */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="text-center mb-14">
            <div className="text-[10px] tracking-[0.25em] uppercase text-amber font-bold mb-3">
              What we stand for
            </div>
            <h2 className="font-display text-4xl md:text-5xl text-cream">
              Three things, <span className="font-serif-italic italic text-amber">forever.</span>
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                icon: Radio,
                title: 'Sound first',
                copy: 'Every track is hand-picked in the cleanest, best-mastered version. Hi-fi only, no compromises.',
              },
              {
                icon: Heart,
                title: 'Independent',
                copy: 'No labels. No ads. Just listeners and a station that answers to no one but the music.',
              },
              {
                icon: Sparkles,
                title: 'Timeless',
                copy: 'From Sinatra to The Stones, from Big Band swing to doo wop harmonies — the catalog is curated, never cluttered.',
              },
            ].map((v, i) => (
              <div key={i} className="rounded-2xl border border-amber/15 bg-espresso/60 p-7 hover:-translate-y-1 hover:border-amber/40 transition-all">
                <div className="w-11 h-11 rounded-full bg-amber/15 flex items-center justify-center mb-5">
                  <v.icon className="w-5 h-5 text-amber" />
                </div>
                <div className="font-display text-2xl text-cream mb-2">{v.title}</div>
                <p className="text-cream/70 font-body leading-relaxed">{v.copy}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
